import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { supabase } from "@/lib/supabase";
import { useAuth } from "@/hooks/useAuth";
import { useActiveSpace } from "@/hooks/useActiveSpace";
import type { MemoryWithChildren } from "@/hooks/useMemories";

interface ToggleVars {
  memoryId: string;
  /** Current state — the mutation flips it. Pass the value rendered in the UI. */
  isFavorited: boolean;
}

/**
 * Toggles the current user's heart on a memory. Optimistically updates the
 * cached memory list so the UI flips instantly; rolls back on error and
 * invalidates on settled to reconcile with the realtime subscription.
 */
export function useToggleFavorite() {
  const qc = useQueryClient();
  const { user } = useAuth();
  const { space } = useActiveSpace();
  const userId = user?.id;
  const spaceId = space?.id ?? null;

  return useMutation({
    mutationFn: async ({ memoryId, isFavorited }: ToggleVars) => {
      if (!userId) throw new Error("Not authenticated");

      if (isFavorited) {
        // Un-favorite — delete the row. RLS limits delete to user's own rows.
        const { error } = await supabase
          .from("memory_favorites")
          .delete()
          .eq("memory_id", memoryId)
          .eq("user_id", userId);
        if (error) throw error;
      } else {
        // Favorite — insert (composite PK prevents duplicates).
        const { error } = await supabase
          .from("memory_favorites")
          .insert({ memory_id: memoryId, user_id: userId });
        if (error) throw error;
      }
    },

    onMutate: async ({ memoryId, isFavorited }) => {
      if (!userId || !spaceId) return;
      const queryKey = ["memories", userId, spaceId] as const;
      await qc.cancelQueries({ queryKey });

      const previous = qc.getQueryData<MemoryWithChildren[]>(queryKey);
      if (previous) {
        qc.setQueryData<MemoryWithChildren[]>(
          queryKey,
          previous.map((m) =>
            m.id === memoryId
              ? {
                  ...m,
                  is_favorited: !isFavorited,
                  favorite_count: Math.max(
                    0,
                    (m.favorite_count ?? 0) + (isFavorited ? -1 : 1),
                  ),
                }
              : m,
          ),
        );
      }
      return { previous, queryKey };
    },

    onError: (err, _vars, ctx) => {
      if (ctx?.previous && ctx.queryKey) {
        qc.setQueryData(ctx.queryKey, ctx.previous);
      }
      toast.error(
        err instanceof Error && err.message
          ? err.message
          : "Could not update favorite",
      );
    },

    onSettled: () => {
      if (!userId || !spaceId) return;
      void qc.invalidateQueries({ queryKey: ["memories", userId, spaceId] });
      void qc.invalidateQueries({ queryKey: ["time-capsules", userId, spaceId] });
    },
  });
}
